import { AnyBoosterType, BoosterType, getBoosterData, PreBoosterType } from '../State/Card/BoosterType';
import { report } from '../report';
import { IPlayerState } from '../State/Player/IPlayerState';
import { Immutable } from './Types';
import { FTUXType } from '../Player/PlayerProperty/Progress/FTUXType';
import { ProductId } from 'Utility/Product';

/**
 * @deprecated We don't need to append to this anymore
 */
// export const upcomingProductIds: string[] = [];

export const currentSundayOfferId: ProductId = ProductId.SUNDAY_OFFER_V2;

export interface IWeightedRewardData {
    odds: number;
    type: RewardType;
    value: number;
}

export interface IAPReward {
    id: ProductId;
    index: number;
    usdPrice: number;
    usdPriceWithoutDiscount?: number;
    usdMultiplier?: number;
    usdPriceScaling?: number;
    rewards: IRewardData[];
}

export interface IRewardData {
    type: RewardType;
    value: number;
    [key: string]: any;
}

export enum RewardType {
    COINS = 'coins',
    BOMB = 'bomb',
    JOKER = 'joker',
    UNDO = 'undo',
    BULLSEYE = 'bullseye',
    SHUFFLE = 'shuffle',
    NONE = 'none',
    PLUS_FIVE_CARDS = 'plus_five_cards',
    THREE_STAR_REWARD = 'three_star_reward',
    PRIZE_WHEEL_SPIN = 'prize_wheel_spin',
    CARDS = 'cards',
    OTHER = 'other',
    // WARNING: this value is also used as storage key
    FREE_PLAY = 'free_play', // New free play booster to replace ad reward when ads are gone
    PET = 'pet',
    PREMIUM_SPIN = 'premium_spin',

    PRE_BOOSTER_REMOVE_THREE = 'pre_booster_remove_three',
    PRE_BOOSTER_JOKER = 'pre_booster_joker',
    PRE_BOOSTER_CANISTER = 'pre_booster_canister',

    FARM_AREA = 'farm_area',
    FARM_ITEM = 'farm_item',
}

export enum RewardPools {
    DAILY_LOGIN = 'dailyLoginRewards',
    CURRENCY_SHARE = 'currencyShareRewards',
    FRIEND_GIFT = 'friendGiftRewards',
}

interface IRewardSettings {
    localizationKey: string;
    texture: string;
    ftuxRequirement?: FTUXType;
}

const rewardDataMap: Record<RewardType, IRewardSettings> = {
    [RewardType.COINS]: {
        localizationKey: 'RewardCoins',
        texture: 'coin-reward',
    },
    [RewardType.BOMB]: getBoosterData(BoosterType.TNT),
    [RewardType.JOKER]: getBoosterData(BoosterType.JOKER),
    [RewardType.UNDO]: getBoosterData(BoosterType.UNDO),
    [RewardType.BULLSEYE]: getBoosterData(BoosterType.BULLS_EYE),
    [RewardType.SHUFFLE]: getBoosterData(BoosterType.SHUFFLE),
    [RewardType.NONE]: null,
    [RewardType.THREE_STAR_REWARD]: null,
    [RewardType.PRIZE_WHEEL_SPIN]: null,
    [RewardType.CARDS]: null,
    [RewardType.OTHER]: null,
    [RewardType.PLUS_FIVE_CARDS]: getBoosterData(BoosterType.PLUS_FIVE),
    [RewardType.FREE_PLAY]: {
        localizationKey: 'FreeLevel',
        texture: 'icon_free_level',
    },
    [RewardType.PET]: null,
    [RewardType.PRE_BOOSTER_REMOVE_THREE]: getBoosterData(PreBoosterType.REMOVE_THREE),
    [RewardType.PRE_BOOSTER_JOKER]: getBoosterData(PreBoosterType.JOKER),
    [RewardType.PRE_BOOSTER_CANISTER]: getBoosterData(PreBoosterType.CANISTER),
    [RewardType.PREMIUM_SPIN]: null,
    [RewardType.FARM_AREA]: null,
    // TODO: Add data for localization and texture for deco item rewards
    [RewardType.FARM_ITEM]: null,
} as const;

export function getRewardData(type: RewardType): IRewardSettings {
    const data = rewardDataMap[type];
    if (!data) {
        report('Unable to find data for given reward type', {
            once: true,
            tags: { type },
        });
        return {
            localizationKey: 'localizationKey',
            texture: 'blank',
        };
    }
    return data;
}

export function isRewardUnlocked(state: Immutable<IPlayerState>, rewardType: RewardType): boolean {
    const requirement = getRewardData(rewardType).ftuxRequirement;
    // Return true if there is no ftux requirement for the given reward type
    return requirement ? state.FTUX.includes(requirement) : true;
}

export const rewardTypeByBoosterType: Record<AnyBoosterType, RewardType | null> = {
    [BoosterType.JOKER]: RewardType.JOKER,
    [BoosterType.UNDO]: RewardType.UNDO,
    [BoosterType.TNT]: RewardType.BOMB,
    [BoosterType.SHUFFLE]: RewardType.SHUFFLE,
    [BoosterType.BULLS_EYE]: RewardType.BULLSEYE,
    [BoosterType.PLUS_FIVE]: RewardType.PLUS_FIVE_CARDS,
    [BoosterType.PLUS_THREE]: null,
    [BoosterType.PLUS_ONE]: RewardType.CARDS,

    [PreBoosterType.REMOVE_THREE]: RewardType.PRE_BOOSTER_REMOVE_THREE,
    [PreBoosterType.JOKER]: RewardType.PRE_BOOSTER_JOKER,
    [PreBoosterType.CANISTER]: RewardType.PRE_BOOSTER_CANISTER,
};

export const boosterTypeByRewardType: Record<RewardType, AnyBoosterType | null> = Object.entries(
    rewardTypeByBoosterType
).reduce((acc, [key, value]: [AnyBoosterType, RewardType | null]) => {
    if (value) {
        acc[value] = key;
    }

    return acc;
}, {} as Record<RewardType, AnyBoosterType | null>);
